import React, {createContext, useContext, useState, useEffect} from 'react';
import {supabase, getUserMetadata} from '../lib/supabase';
import {apiService} from '../services/api';
import {User} from '../types';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isSyncingProfile?: boolean;
    login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    loginWithFacebook: () => Promise<void>;
    signup: (name: string, email: string, password: string) => Promise<{ requiresEmailConfirmation: boolean }>;
    logout: () => Promise<void>;
    purchaseTokens: (amount: number) => Promise<void>;
    useTokens: (propertyId: string) => Promise<boolean>;
    refreshUser: () => Promise<void>;
    setRedirectPath: (path: string) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncingProfile, setIsSyncingProfile] = useState(false);
    const [redirectPath, setRedirectPath] = useState<string | null>(null);

    // Fetch user profile from backend
    const fetchUserProfile = async (): Promise<User | null> => {
        try {
            setIsSyncingProfile(true);
            const userProfile = await apiService.getUserProfile();
            setIsSyncingProfile(false);
            return userProfile;
        } catch (error: any) {
            setIsSyncingProfile(false);
            const isBlocked = error?.status === 403 && /blokiran/i.test(error?.message || '');
            if (isBlocked) {
                sessionStorage.setItem('authErrorCode', 'BLOCKED_ACCOUNT');
                await supabase.auth.signOut();
                setUser(null);
            }
            return null;
        }
    };

    // Handle redirect after successful login
    const handleSuccessfulLogin = () => {
        if (window.location.pathname === '/reset-password') {
            setIsLoading(false);
            return;
        }
        const redirectTo = sessionStorage.getItem('redirectAfterAuth');
        if (redirectTo) {
            sessionStorage.removeItem('redirectAfterAuth');

            // Avoid redirect loops or unnecessary reloads
            const current = window.location.pathname + window.location.search + window.location.hash;
            if (redirectTo === current || redirectTo === window.location.pathname) {
                setIsLoading(false);
                return;
            }

            // Perform a single redirect
            setTimeout(() => {
                window.location.replace(redirectTo);
            }, 100);
        } else {
            // No redirect needed, set loading to false
            setIsLoading(false);
        }
    };

    // Initialize auth state
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const {data: {session}} = await supabase.auth.getSession();

                if (session) {
                    // Optimistically set a lightweight user from Supabase while backend profile loads
                    const basicUser: User = {
                        id: session.user.id,
                        name: getUserMetadata(session.user as any).name || (session.user.email || 'Korisnik'),
                        email: session.user.email || '',
                        tokens: 0,
                        favorites: [],
                        listings: [],
                        purchasedContacts: [],
                        avatar: getUserMetadata(session.user as any).avatarUrl || undefined,
                        provider: getUserMetadata(session.user as any).provider,
                    };
                    setUser(basicUser);

                    // Fetch full profile in background
                    fetchUserProfile()
                        .then((profile) => {
                            if (profile) {
                                setUser(profile);
                                handleSuccessfulLogin();
                                return;
                            }
                            const authErrorCode = sessionStorage.getItem('authErrorCode');
                            if (authErrorCode) {
                                setIsLoading(false);
                            } else {
                                handleSuccessfulLogin();
                            }
                        })
                        .catch(() => {});
                }
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();

        // Listen for auth changes
        const {data: {subscription}} = supabase.auth.onAuthStateChange(
            async (event, session) => {

                if (event === 'SIGNED_IN' && session) {
                    try {
                        // Optimistically set basic user immediately
                        const basicUser: User = {
                            id: session.user.id,
                            name: getUserMetadata(session.user as any).name || (session.user.email || 'Korisnik'),
                            email: session.user.email || '',
                            tokens: 0,
                            favorites: [],
                            listings: [],
                            purchasedContacts: [],
                            avatar: getUserMetadata(session.user as any).avatarUrl || undefined,
                            provider: getUserMetadata(session.user as any).provider,
                        };
                        setUser(basicUser);

                        // Fetch full profile in background
                        fetchUserProfile()
                            .then((profile) => {
                                if (profile) {
                                    setUser(profile);
                                    handleSuccessfulLogin();
                                    return;
                                }
                                const authErrorCode = sessionStorage.getItem('authErrorCode');
                                if (authErrorCode) {
                                    setIsLoading(false);
                                } else {
                                    handleSuccessfulLogin();
                                }
                            })
                            .catch(() => {});
                    } catch (error) {
                        setUser(null);
                        setIsLoading(false);
                    }
                } else if (event === 'SIGNED_OUT') {
                    setUser(null);
                    setIsLoading(false);
                } else {
                    // For other events, just set loading to false
                    setIsLoading(false);
                }
            }
        );

        return () => subscription.unsubscribe();
    }, []);

    const login = async (email: string, password: string, rememberMe: boolean = false) => {
        setIsLoading(true);
        try {

            const {data, error} = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                throw error;
            }

            const profile = await fetchUserProfile();
            if (!profile) {
                const authErrorCode = sessionStorage.getItem('authErrorCode');
                if (authErrorCode) {
                    throw new Error(authErrorCode);
                }
            }

            // Handle remember me by updating session persistence
            if (rememberMe && data.session) {
                // Store session info for 7 days
                const expirationTime = new Date().getTime() + (7 * 24 * 60 * 60 * 1000); // 7 days
                localStorage.setItem('supabase.auth.remember', expirationTime.toString());
            }

            // Don't set isLoading to false here - let the auth state listener handle it
            // The auth state change listener will fetch user profile and handle redirect

        } catch (error) {
            setIsLoading(false); // Only set to false on error
            throw error;
        }
    };

    const loginWithGoogle = async () => {
        setIsLoading(true);
        try {
            const {error} = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                },
            });

            if (error) throw error;

            // OAuth redirect will handle the rest
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const loginWithFacebook = async () => {
        setIsLoading(true);
        try {
            const {data, error} = await supabase.auth.signInWithOAuth({
                provider: 'facebook',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                },
            });

            if (error) throw error;

            // OAuth redirect will handle the rest
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const signup = async (name: string, email: string, password: string) => {
        setIsLoading(true);
        try {
            const {data, error} = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: name,
                    },
                },
            });

            if (error) throw error;

            // Check if email confirmation is required
            const requiresEmailConfirmation = !!(data.user && !data.session);
            return { requiresEmailConfirmation };
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            setIsLoading(true);
            const {error} = await supabase.auth.signOut();
            if (error) throw error;
            setUser(null);
            setRedirectPath(null);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            throw error;
        }
    };

    const purchaseTokens = async (amount: number) => {
        try {
            const result = await apiService.purchaseTokens(amount);

            // Refresh user data
            await refreshUser();

        } catch (error) {
            throw error;
        }
    };

    const refreshUser = async () => {
        try {
            const userProfile = await apiService.refreshUser();
            setUser(userProfile);
            return userProfile;
        } catch (error) {
            // Silent fail - user will stay with current data
            return null;
        }
    };

    const useTokens = async (propertyId: string): Promise<boolean> => {
        try {
            const result = await apiService.useTokens(propertyId);

            // Refresh user data to get updated purchasedContacts
            // Wait for refresh to complete
            const updatedUser = await refreshUser();
            
            // Verify the purchase was recorded
            if (updatedUser && !updatedUser.purchasedContacts?.includes(propertyId)) {
                // If not in the list yet, try refreshing one more time
                await new Promise(resolve => setTimeout(resolve, 500));
                await refreshUser();
            }

            return true;
        } catch (error: any) {
            throw error; // Re-throw so PropertyDetailsPage can handle it
        }
    };

    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        isSyncingProfile,
        login,
        loginWithGoogle,
        loginWithFacebook,
        signup,
        logout,
        purchaseTokens,
        useTokens,
        refreshUser,
        setRedirectPath,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
